import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack
} from '@mui/material';

const HeroSection = ({ onNavigateToAuth }) => {
  return (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: 'calc(100vh - 56px)', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Cool Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: { xs: 0.08, md: 0.1 }
      }}>
        {/* Large Circle */}
        <Box sx={{
          position: 'absolute',
          top: { xs: '5%', md: '10%' },
          right: { xs: '-10%', md: '10%' },
          width: { xs: 180, md: 300 },
          height: { xs: 180, md: 300 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f6ad55, #ff8a65)',
          animation: 'float 6s ease-in-out infinite'
        }} />
        
        {/* Medium Circle */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: '15%', md: '20%' },
          left: { xs: '-5%', md: '15%' },
          width: { xs: 120, md: 200 },
          height: { xs: 120, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #4fc3f7, #29b6f6)',
          animation: 'float 8s ease-in-out infinite reverse'
        }} />
        
        {/* Small Circle */}
        <Box sx={{
          position: 'absolute',
          top: { xs: '55%', md: '60%' },
          right: { xs: '10%', md: '25%' },
          width: { xs: 80, md: 120 },
          height: { xs: 80, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ab47bc, #8e24aa)',
          animation: 'float 10s ease-in-out infinite',
          display: { xs: 'none', sm: 'block' }
        }} />
        
        {/* Triangle */}
        <Box sx={{
          position: 'absolute',
          top: { xs: '20%', md: '25%' },
          left: { xs: '5%', md: '20%' },
          width: 0,
          height: 0,
          borderLeft: { xs: '40px solid transparent', md: '60px solid transparent' },
          borderRight: { xs: '40px solid transparent', md: '60px solid transparent' },
          borderBottom: { xs: '70px solid #66bb6a', md: '100px solid #66bb6a' },
          opacity: 0.6,
          animation: 'rotate 20s linear infinite',
          display: { xs: 'none', sm: 'block' }
        }} />
        
        {/* Square */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: '10%', md: '15%' },
          right: { xs: '20%', md: '35%' },
          width: { xs: 50, md: 80 },
          height: { xs: 50, md: 80 },
          background: 'linear-gradient(135deg, #ff7043, #ff5722)',
          borderRadius: 2,
          transform: 'rotate(45deg)',
          animation: 'float 12s ease-in-out infinite',
          display: { xs: 'none', md: 'block' }
        }} />

        {/* Keyframes for animations */}
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-20px); }
            }
            @keyframes rotate {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ 
        maxWidth: { xs: '100%', sm: '600px', md: '800px' }, 
        mx: 'auto', 
        px: { xs: 2, sm: 3, md: 3 }, 
        position: 'relative', 
        zIndex: 1 
      }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 3, md: 4 },
            lineHeight: { xs: 1.2, md: 1.1 }
          }}
        >
          Your beauty business{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#f6ad55',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            simplified.
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 4, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Powerful, affordable, intuitive!
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          justifyContent="center"
          sx={{ px: { xs: 2, sm: 0 } }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#4a5568',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 4, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '1rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#2d3748'
              }
            }}
          >
            Get started for free
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 3, md: 4 },
            fontSize: { xs: '0.85rem', md: '0.9rem' }
          }}
        >
          No credit card required
        </Typography>
      </Box>
    </Box>
  );
};

export default HeroSection;
